import React, { useCallback, useContext, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { CONTENT_LIBRARY_OPENED_TABLE_PATH, CONTENT_LIBRARY_PATH } from 'constants/routes';
import { EDITING_PERMISSION_BLOCK, EDITING_PERMISSION_TEXT, GENERAL_ERROR_MESSAGE } from 'constants/texts';
import useQueryParams from 'hooks/useQueryParams';
import { useSnackbar } from 'hooks/useSnackbar';
import { getSamplesItinerariesThunk, getSamplesLookboksThunk } from 'store/admin/itineraries/thunks';
import { resetPagesTableAction, resetSearchResultCountersAction } from 'store/admin/pagesTable';
import {
  pagesTableListSelector,
  pagesTableMetaSelector,
  searchFiltersSelector,
  searchResultCountersSelector,
} from 'store/admin/pagesTable/selectors';
import { getContentLibrarySearchCountersThunk, getPagesTableThunk } from 'store/admin/pagesTable/thunks';
import { isItineraries, isLookbook } from 'utils/itineraries/functions';

import { useTableCollapsing, useUpdatingSearchResultCounters } from '../shared/functions';
import {
  useContentLibraryQuery,
  useCustomFilters,
  usePageActivation,
  usePageDeleting,
  usePageDuplicating,
  usePreviewModalData,
} from './functions';

export const ContentLibraryContext = React.createContext(null);
export const useContentLibraryContext = () => useContext(ContentLibraryContext);

export const useContentLibraryContextValue = ({ content }) => {
  const dispatch = useDispatch();
  const onSnackbar = useSnackbar();
  const { queryParams, setSort, setPage, setLimit } = useQueryParams();
  const query = useContentLibraryQuery({ content });
  useUpdatingSearchResultCounters(getContentLibrarySearchCountersThunk, resetSearchResultCountersAction);
  useCustomFilters();

  const pages = useSelector(pagesTableListSelector);
  const meta = useSelector(pagesTableMetaSelector);
  const searchResultCounters = useSelector(searchResultCountersSelector);
  const searchFilters = useSelector(searchFiltersSelector);

  const [isLoading, setIsLoading] = useState(false);

  const fetchPages = useCallback(() => dispatch(getPagesTableThunk(query)), [dispatch, query]);
  const fetchSamplesItineraries = useCallback(() => dispatch(getSamplesItinerariesThunk(query)), [dispatch, query]);
  const fetchSamplesLookbooks = useCallback(() => dispatch(getSamplesLookboksThunk(query)), [dispatch, query]);

  const { duplicatePage, duplicateSystemWideSample } = usePageDuplicating({ setIsLoading });
  const { deletePage } = usePageDeleting({ pages, fetchPages });
  const { onToggleActive } = usePageActivation({ fetchPages, setIsLoading });
  const handleCollapse = useTableCollapsing({
    content,
    collapsedTablesRoute: CONTENT_LIBRARY_PATH,
    expandTableRoute: CONTENT_LIBRARY_OPENED_TABLE_PATH,
  });

  const { previewModalData, openPreviewModal, closePreviewModal } = usePreviewModalData();

  const fetchTable = useCallback(async () => {
    if (isItineraries(content)) {
      await fetchSamplesItineraries();
    } else if (isLookbook(content)) {
      await fetchSamplesLookbooks();
    } else {
      await fetchPages();
    }
  }, [content, fetchSamplesItineraries, fetchSamplesLookbooks, fetchPages]);

  const tryToRefetchTable = useCallback(() => {
    if (!content) {
      return;
    }

    const run = async () => {
      setIsLoading(true);
      try {
        await fetchTable();
      } catch (error) {
        dispatch(resetPagesTableAction());
        if (error?.errors?.title?.includes(EDITING_PERMISSION_TEXT)) {
          onSnackbar(EDITING_PERMISSION_BLOCK, true);
        } else {
          onSnackbar(GENERAL_ERROR_MESSAGE, true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    run();
  }, [content, dispatch, fetchTable, onSnackbar]);

  const handleAfterSaveFilter = useCallback(
    (table) => {
      if (table === content) {
        tryToRefetchTable();
      }

      if (queryParams.q) {
        dispatch(getContentLibrarySearchCountersThunk(queryParams.q));
      }
    },
    [content, tryToRefetchTable, queryParams.q, dispatch],
  );

  useEffect(() => {
    tryToRefetchTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, content, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetPagesTableAction());
    };
  }, [dispatch]);

  return {
    content,
    pages,
    searchResultCounters,
    searchFilters,
    handleAfterSaveFilter,
    queryParams,
    setSort,
    setPage,
    setLimit,
    deletePage,
    duplicatePage,
    isLoading,
    pagination: meta,
    previewModalData,
    closePreviewModal,
    openPreviewModal,
    onToggleActive,
    duplicateSystemWideSample,
    handleCollapse,
  };
};
